import { addClassToElement } from '@jspartials/class-list-utils';
import { createElement } from '@jspartials/dom-api-utils';
const selectors = {
  countdownSeparatorClass: 'hw-countdown__separator',
  countdownTimeClass: 'hw-countdown__time',
  countdownLabelClass: 'hw-countdown__label'
};

const TYPES = {
  Normal: 'normal',
  Simple: 'simple'
};

export default class Countdown {
  constructor(
    container,
    startAt,
    endAt,
    showDays = false,
    showLabels = false,
    type = TYPES.Normal,
    labels = {}
  ) {
    this.container = container;
    this.startAt = Date.parse(startAt);
    this.endAt = Date.parse(endAt);
    this.remainingTime = this.endAt - Date.now();
    this.showDays = showDays;
    this.showLabels = showLabels;
    this.interval = null;
    this.type = type;
    this.labels = labels;

    this.time = {
      days: {
        element: null,
        value: 0,
        class: 'days'
      },
      hours: {
        element: null,
        value: 0,
        class: 'hours'
      },
      minutes: {
        element: null,
        value: 0,
        class: 'minutes'
      },
      seconds: {
        element: null,
        value: 0,
        class: 'seconds'
      }
    };

    this.init();
  }

  init() {
    if (
      isNaN(this.startAt) ||
      isNaN(this.endAt) ||
      this.startAt > this.endAt ||
      this.startAt > Date.now() ||
      this.remainingTime <= 0
    ) {
      this.container.remove();
      this.container = null;
      return;
    }

    this.container.classList.remove('tw-hidden');

    const checkTimeAndUpdate = () => {
      if (this.remainingTime > 1000) {
        this.remainingTime = this.endAt - Date.now();
        this.updateCountdown();
      }
    };

    // Call the function so the countdown is visible right away
    checkTimeAndUpdate();

    this.interval = setInterval(checkTimeAndUpdate, 1000);
  }

  createTimeBlock = (value, unit, canShowDays) => {
    if (canShowDays) {
      return document.createTextNode('');
    }

    const wrapper = document.createElement('div');
    const time = createElement({
      tag: 'p',
      content: value,
      classes: [
        selectors.countdownTimeClass,
        `js-countdown__${unit.toLowerCase()}`
      ]
    });

    wrapper.appendChild(time);

    if (this.showLabels) {
      const label = createElement({
        tag: 'p',
        content: this.labels[unit],
        classes: [
          selectors.countdownLabelClass,
          `js-countdown__${unit.toLowerCase()}`
        ]
      });
      wrapper.appendChild(label);
    }

    return wrapper;
  };

  createSimpleTimeBlock = (value, unit, canShowDays) => {
    if (canShowDays) {
      return document.createTextNode('');
    }

    const wrapper = document.createDocumentFragment();

    const time = createElement({
      tag: 'span',
      content: value,
      classes: [
        selectors.countdownTimeClass,
        `js-countdown__${unit.toLowerCase()}`
      ]
    });
    wrapper.appendChild(time);

    const label = createElement({
      tag: 'label',
      content: this.labels[unit],
      classes: [selectors.countdownLabelClass]
    });
    wrapper.appendChild(label);

    return wrapper;
  };

  generateSeparators(template, unit, index) {
    const last = index === Object.keys(this.time).length - 1;

    // Add separators (:)
    if (!last && this.type !== TYPES.Simple) {
      if (unit.toLowerCase() === 'days' && this.showDays) {
        const separator = document.createElement('p');
        separator.classList.add(selectors.countdownSeparatorClass);
        separator.innerHTML = ':';
        template.appendChild(separator);
      } else if (unit.toLowerCase() !== 'days') {
        const separator = document.createElement('p');
        separator.classList.add(selectors.countdownSeparatorClass);
        separator.innerHTML = ':';
        template.appendChild(separator);
      }
    }
  }

  createTemplate() {
    const template = document.createDocumentFragment();

    Object.keys(this.time).forEach((unit, index) => {
      const unitObject = this.time[unit];
      const isDaysHidden = unit.toLowerCase() === 'days' && !this.showDays;

      template.appendChild(
        this.type === TYPES.Simple
          ? this.createSimpleTimeBlock(
              unitObject.value,
              unitObject.class,
              isDaysHidden
            )
          : this.createTimeBlock(
              unitObject.value,
              unitObject.class,
              isDaysHidden
            )
      );

      this.generateSeparators(template, unit, index);
    });

    this.container.appendChild(template);

    if (this.container.parentElement.classList.contains('plp')) {
      addClassToElement(this.container, 'animate');
    }

    // Assign elements
    Object.keys(this.time).forEach((unit) => {
      this.time[unit] = {
        ...this[unit],
        element: this.container.querySelector(
          `.js-countdown__${this.time[unit].class}`
        )
      };
    });
  }

  updateTemplate() {
    if (
      this.showDays &&
      this.time.days.element &&
      this.time.days.value.trim() !== this.time.days.element.innerText.trim()
    ) {
      this.time.days.element.innerHTML = this.time.days.value;
    }

    if (
      this.time.hours.element &&
      this.time.hours.value.trim() !== this.time.hours.element.innerText.trim()
    ) {
      this.time.hours.element.innerHTML = this.time.hours.value;
    }

    if (
      this.time.minutes.element &&
      this.time.minutes.value.trim() !==
        this.time.minutes.element.innerText.trim()
    ) {
      this.time.minutes.element.innerHTML = this.time.minutes.value;
    }

    if (
      this.time.seconds.element &&
      this.time.seconds.value.trim() !==
        this.time.seconds.element.innerText.trim()
    ) {
      this.time.seconds.element.innerHTML = this.time.seconds.value;
    }
  }

  updateCountdown() {
    const seconds_ms = 1000,
      minutes_ms = seconds_ms * 60,
      hours_ms = minutes_ms * 60,
      days_ms = hours_ms * 24;

    const rawDays = this.remainingTime / days_ms,
      rawHours = this.showDays
        ? (this.remainingTime % days_ms) / hours_ms
        : this.remainingTime / hours_ms,
      rawMinutes = (this.remainingTime % hours_ms) / minutes_ms,
      rawSeconds = (this.remainingTime % minutes_ms) / seconds_ms;

    this.time.days.value = Math.floor(rawDays).toString().padStart(2, '0');
    this.time.hours.value = Math.floor(rawHours).toString().padStart(2, '0');
    this.time.minutes.value = Math.floor(rawMinutes)
      .toString()
      .padStart(2, '0');
    this.time.seconds.value = Math.floor(rawSeconds)
      .toString()
      .padStart(2, '0');

    if (this.container.innerHTML.trim().length > 0) {
      this.updateTemplate();
    } else {
      this.createTemplate();
    }

    if (this.remainingTime <= 1000 || this.container === null) {
      clearInterval(this.interval);
    }
  }
}
